import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Italian is the primary audience and the fallback language — it ships in
// the critical bundle. en/es (~40K each) load on demand (change 048, CWV):
// bundling all three locales statically put ~80K of dead JSON in the
// landing page's main chunk.
import it from './locales/it.json'

const lazyLocales: Record<string, () => Promise<unknown>> = {
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
}

export async function ensureLocaleLoaded(lng: string): Promise<void> {
  const base = (lng || '').split('-')[0]
  const loader = lazyLocales[base]
  if (!loader || i18n.hasResourceBundle(base, 'translation')) return
  try {
    const mod: any = await loader()
    i18n.addResourceBundle(base, 'translation', mod.default ?? mod, true, true)
  } catch (error) {
    console.error(`Failed to load locale ${base}:`, error)
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      it: {
        translation: it,
      },
    },
    // en/es arrive via addResourceBundle — don't treat them as missing
    partialBundledLanguages: true,
    fallbackLng: 'it', // Default to Italian (Normattiva is Italian law platform)
    supportedLngs: ['en', 'it', 'es'],
    // Define which languages are RTL — handled by useRTL hook, not i18next's deprecated `rtl` option
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    // Enable debug mode in development
    debug: process.env.NODE_ENV === 'development',
    // Fallback to key if translation is missing
    fallbackNS: ['translation'],
    // Set default namespace
    defaultNS: 'translation',
    // Handle missing keys - in production, you might want to log these
    missingKeyHandler: (_lng, _ns, key) => {
      console.warn(`Missing translation key: ${key} for language: ${_lng}`);
    },
    // Set to true if you want to catch key references that are not defined
    saveMissing: false,
  })
  .then(async () => {
    // If the detected language is a lazy one (en/es), fetch its bundle and
    // re-emit languageChanged so already-mounted components re-render
    const detected = i18n.language
    if (detected && !i18n.hasResourceBundle(detected.split('-')[0], 'translation')) {
      await ensureLocaleLoaded(detected)
      await nativeChangeLanguage(detected)
    }
    console.log('i18n initialized successfully');
  })
  .catch((error) => {
    console.error('Error initializing i18n:', error);
  });

// Every language switch (useLanguage, LanguageSelector, ...) goes through
// i18n.changeLanguage — make it load the lazy bundle first so the UI never
// renders raw translation keys
const nativeChangeLanguage = i18n.changeLanguage.bind(i18n)
i18n.changeLanguage = (async (lng?: string, callback?: any) => {
  if (lng) await ensureLocaleLoaded(lng)
  return nativeChangeLanguage(lng, callback)
}) as typeof i18n.changeLanguage

export default i18n
export const changeLanguage = (lng: string) => i18n.changeLanguage(lng)
